/**
 * The sidebar's FOLDER TREE.
 *
 * The document catalog (RFC 0263) is flat: every row carries a display `path`
 * like "notes.md" or "reviews/feat-x/summary.md". The sidebar used to render
 * that list verbatim, so a project with any structure at all read as a pile of
 * files with slashes in their names. This turns those paths back into the tree
 * they already describe.
 *
 * Pure over DocumentRef[], so the nesting rules are unit-testable without a
 * repo, React, or wasm — the same reason `partitionSidebarItems` is pure.
 *
 * ORDERING. Folders before files, each alphabetical and case-insensitive. NOT
 * the catalog's recency order: a tree whose branches reshuffle as documents are
 * touched cannot be navigated by memory, and recency is what the Home view is
 * for.
 */

import type { DocumentRef, SkillScope, WorldSkillRef } from '@mod/core/model'
import type { ShownSkill } from '../../services/skills/skillCatalog'

export interface TreeFile {
  kind: 'file'
  /** The last path segment, or the title when the path has no structure. */
  name: string
  doc: DocumentRef
}

export interface TreeFolder {
  kind: 'folder'
  name: string
  /** Full path from the root ("reviews/feat-x") — the expand-state key. */
  path: string
  children: TreeNode[]
  /** Present when this folder has a durable entry, even if it is empty. */
  explicitId?: string
  /**
   * Open tasks, drawn on the right of the row (RFC 0319 3d). Only a
   * PROJECT root carries one: an ordinary folder holds documents, and a task
   * belongs to the project rather than to wherever its document happens to sit.
   */
  count?: { open: number; waiting: number }
  /**
   * This folder IS a project (RFC 0281 S2). It opens by default, because the
   * files under it are the directory itself, not a drawer to be found.
   */
  root?: true
  /**
   * One of a project's four folders (RFC 0277 S12): what fills it, when it is
   * not documents split by path. `code` has no children here; the git tree
   * draws itself under it, lazily.
   */
  source?: ProjectFolderSource
  /** On a source folder, the stable source identity whose local files it draws. */
  sourceId?: string
}

export type ProjectFolderSource =
  | 'sessions'
  | 'apps'
  | 'documents'
  | 'skills'
  | 'code'

/** A session or an app inside a project's folder (RFC 0277 S12). */
export interface TreeItem {
  kind: 'item'
  name: string
  /** Unique in the tree, for the row's key. */
  path: string
  item:
    | { type: 'session'; id: string; branch?: string }
    | { type: 'app'; id: string; viewDocId: string; name: string }
    /** Standing instructions (RFC 0334 3g): the World's soul file, or one
     *  project's. Always on, and opened in the editor. `documentId` is absent
     *  until the first open makes the document from the old string. */
    | {
        type: 'standing'
        level: 'world' | 'project'
        projectId?: string
        documentId?: string
      }
    /** A World or project skill (RFC 0334 3g), opened in the editor. */
    | { type: 'skill'; skill: SidebarSkill }
  /** The project's History (RFC 0313 3i): one timeline, opened as a pane. */
}

/** A skill as a row needs it. A bare World ref is a World skill. */
export type SidebarSkill = WorldSkillRef &
  Partial<Pick<ShownSkill, 'appId' | 'shadows'>> & { scope?: SkillScope }

export type TreeNode = TreeFolder | TreeFile | TreeItem

/** Path segments, tolerating leading/trailing/doubled slashes. */
export function pathSegments(path: string): string[] {
  return path.split('/').filter((s) => s.length > 0)
}

const byName = (a: TreeNode, b: TreeNode) =>
  a.name.toLowerCase().localeCompare(b.name.toLowerCase())

const sortNodes = (nodes: TreeNode[]): TreeNode[] => {
  const folders = nodes.filter((n): n is TreeFolder => n.kind === 'folder')
  const files = nodes.filter((n): n is TreeFile => n.kind === 'file')
  const items = nodes.filter((n): n is TreeItem => n.kind === 'item')
  for (const f of folders) if (!f.root) f.children = sortNodes(f.children)
  return [...folders.sort(byName), ...files.sort(byName), ...items]
}

export function buildFileTree(
  docs: DocumentRef[],
  explicitFolders: { id: string; path: string }[] = [],
): TreeNode[] {
  const root: TreeNode[] = []
  // Folders are keyed by full path so "a/x" and "b/x" stay distinct.
  const folders = new Map<string, TreeFolder>()

  const folderAt = (segments: string[]): TreeNode[] => {
    let siblings = root
    let prefix = ''
    for (const segment of segments) {
      prefix = prefix ? `${prefix}/${segment}` : segment
      let folder = folders.get(prefix)
      if (!folder) {
        folder = { kind: 'folder', name: segment, path: prefix, children: [] }
        folders.set(prefix, folder)
        siblings.push(folder)
      }
      siblings = folder.children
    }
    return siblings
  }

  for (const entry of explicitFolders) {
    const segments = pathSegments(entry.path)
    folderAt(segments)
    const folder = folders.get(segments.join('/'))
    if (folder) folder.explicitId = entry.id
  }

  for (const doc of docs) {
    const segments = pathSegments(doc.path)
    // A file's own name is the last segment; everything before it is structure.
    const name = segments.pop() ?? doc.title
    folderAt(segments).push({ kind: 'file', name, doc })
  }

  return sortNodes(root)
}

/** The folder name and expand-state key for the World's skills. */
export const SKILLS_FOLDER = 'Skills'

/** A skill as a row in a Skills folder. */
function skillRow(folderPath: string, skill: SidebarSkill): TreeItem {
  return {
    kind: 'item',
    name: skill.title,
    path: `${folderPath}/${skill.skillId}`,
    item: { type: 'skill', skill },
  }
}

/**
 * The World's Skills folder, for the top of the tree (RFC 0334 3g): your soul
 * file, then your World skills. A project's own skills are in that project's
 * Skills folder (`projectFolders`), because they belong to it and to the
 * people in it, not to you.
 *
 * SYNTHETIC, and not derived from document paths. A World skill's document is
 * catalogued in whichever project wrote it, so deriving this folder from paths
 * would show the skills THIS project wrote, the wrong set in every project but
 * one.
 *
 * THE SOUL FILE FIRST. It is in every turn; a skill is loaded when needed. So
 * the row every agent always reads leads, marked always on by the row itself.
 *
 * App and Mod skills are not here. An app's skill is read from the app, and
 * Mod's in Settings; a person does not pick either.
 *
 * Null with no skills and no soul row, so a tree that asks for neither is
 * unchanged.
 */
export function skillsFolder(
  skills: readonly SidebarSkill[],
  opts: { standing?: boolean; soulDocId?: string } = {},
): TreeFolder | null {
  const children: TreeNode[] = opts.standing
    ? [
        {
          kind: 'item',
          name: 'Soul',
          path: `${SKILLS_FOLDER}/:soul`,
          item: {
            type: 'standing',
            level: 'world',
            ...(opts.soulDocId ? { documentId: opts.soulDocId } : {}),
          },
        },
      ]
    : []
  for (const skill of skills) children.push(skillRow(SKILLS_FOLDER, skill))
  if (children.length === 0) return null
  return { kind: 'folder', name: SKILLS_FOLDER, path: SKILLS_FOLDER, children }
}

/** The pre-home fallback directory, including legacy synthetic skills. */
export function buildDirectory(
  docs: DocumentRef[],
  skills: SidebarSkill[] = [],
): TreeNode[] {
  const tree = buildFileTree(docs)
  const folder = skillsFolder(skills)
  return folder ? [folder, ...tree] : tree
}

/** One project's fill to the directory (RFC 0281 S2). */
export interface DirectoryProject {
  projectId: string
  /** What the World's registry calls it, which is what the root row shows. */
  name: string
  /** An edge in the World mount tree. Absent means top level. */
  parent?: { projectId: string; folder: string }
  docs: DocumentRef[]
  /** Explicit folder entries, including empty ones (RFC 0357 S8). */
  folders?: { id: string; path: string }[]
  /** Its sessions, newest first (RFC 0277 S12). */
  sessions?: { id: string; title: string; branch?: string }[]
  /** Its installed apps. */
  apps?: { id: string; name: string; viewDocId: string }[]
  /** Its own skills (RFC 0334 3g), drawn in its Skills folder. */
  skills?: SidebarSkill[]
  /** Draw its instructions row, first in its Skills folder. */
  standing?: boolean
  /** Its instructions document, once there is one. */
  instructionsDocId?: string
  /** Whether it has a repository, which is what draws its Code folder. */
  hasCode?: boolean
  sources?: { id: string; name: string; folder?: string }[]
  /** Its task registry, counted (RFC 0319 3d). Absent draws no count. */
  count?: { open: number; waiting: number }
}

function documentAndSourceNodes(
  docs: DocumentRef[],
  sources: DirectoryProject['sources'],
  folders: DirectoryProject['folders'] = [],
): TreeNode[] {
  const children = buildFileTree(docs, folders)
  for (const source of sources ?? []) {
    // An unplaced source is still a folder in this tree, beside documents.
    // Its row uses the source ID until placed: names are not unique paths.
    if (!source.folder) {
      children.push({
        kind: 'folder',
        name: source.name.replace(/\//g, '-'),
        path: `:source/${source.id}`,
        source: 'code',
        sourceId: source.id,
        children: [],
      })
      continue
    }
    const segments = pathSegments(source.folder)
    let siblings = children
    for (const [index, segment] of segments.entries()) {
      const path = segments.slice(0, index + 1).join('/')
      if (index === segments.length - 1) {
        siblings.push({
          kind: 'folder',
          name: segment,
          path,
          source: 'code',
          sourceId: source.id,
          children: [],
        })
        break
      }
      let folder = siblings.find(
        (node): node is TreeFolder =>
          node.kind === 'folder' && node.path === path && !node.source,
      )
      if (!folder) {
        folder = { kind: 'folder', name: segment, path, children: [] }
        siblings.push(folder)
      }
      siblings = folder.children
    }
  }
  return sortNodes(children)
}

/** Project documents and placed sources are peers in one folder tree. */
export function projectFolders(project: DirectoryProject): TreeNode[] {
  // A caller that names no sources gets the legacy default. An EMPTY list is
  // not that: the project said it has none, as one does after its repo moved
  // to another project (RFC 0357 S14) or into a child (S5). Drawing "Project
  // files" there resurrects the moved source as a row, and the row's "default"
  // source answers from this device's stale mount of it.
  const sources =
    project.sources ??
    (project.hasCode ? [{ id: 'default', name: 'Project files' }] : undefined)
  return documentAndSourceNodes(project.docs, sources, project.folders)
}

/**
 * The directory across every project in the World (RFC 0281 S2).
 *
 * EVERY PROJECT GETS A ROOT, including the only one. RFC 0281 rendered a single
 * project flat so that binding a window to the World would not look like a
 * regression, and that held until the row had something to SAY: RFC 0319 3d
 * puts the project's task count on it, and a count with no row to sit on is
 * invisible in exactly the window most people have. The root opens by default
 * (see `FilesView`), so the tree below it reads as it always did.
 *
 * A ROOT IS A FOLDER, not a new kind of row: the tree already collapses, opens,
 * and remembers its state per path, and a project root that behaved differently
 * from every other folder would be a second mechanism for the same gesture
 * (RFC 0281 §3 wants "every project, each a collapsible root").
 *
 * THE ROOT'S PATH IS THE PROJECT ID, not its display name. Expand state is keyed
 * by path, and two projects can share a name, so naming the folder by id keeps
 * their state apart and survives a rename. The name is what is DRAWN.
 *
 * Skills stay at the very top, once, above every project: they are deployment
 * scoped, so filing them under one project would say they belong to it.
 *
 * Projects are rendered in the order given. `projectWorldProjects` already
 * sorts by display name, and re-sorting here on a name this function also draws
 * would bury that decision in two places.
 */
export function buildWorldDirectory(
  projects: DirectoryProject[],
  skills: SidebarSkill[] = [],
  opts: {
    standing?: boolean
    soulDocId?: string
    home?: {
      projectId: string
      docs: DocumentRef[]
      folders?: DirectoryProject['folders']
      sources?: DirectoryProject['sources']
    }
  } = {},
): TreeNode[] {
  const homeNodes = opts.home
    ? documentAndSourceNodes(
        opts.home.docs,
        opts.home.sources,
        opts.home.folders,
      )
    : []
  const skillsRoot = opts.home ? null : skillsFolder(skills, opts)
  // Only a World not yet placed has a soul row. A placed World's soul is the
  // root AGENTS.md, an ordinary file row that drags like any other (RFC 0357
  // 3i), and names no id: with no id, "home does not list it" was always true
  // and a row that could not move was drawn over the real file.
  if (
    opts.home &&
    opts.standing &&
    opts.soulDocId &&
    !opts.home.docs.some((doc) => doc.id === opts.soulDocId)
  ) {
    let folder = homeNodes.find(
      (node): node is TreeFolder =>
        node.kind === 'folder' && node.path === 'skills',
    )
    if (!folder) {
      folder = { kind: 'folder', name: 'skills', path: 'skills', children: [] }
      homeNodes.push(folder)
    }
    folder.children.unshift({
      kind: 'item',
      name: 'Soul',
      path: 'skills/:soul',
      item: {
        type: 'standing',
        level: 'world',
        ...(opts.soulDocId ? { documentId: opts.soulDocId } : {}),
      },
    })
  }

  const roots: TreeFolder[] = projects.map((project) => ({
    kind: 'folder' as const,
    name: project.name || project.projectId,
    path: project.projectId,
    children: projectFolders(project),
    root: true as const,
    ...(project.count ? { count: project.count } : {}),
  }))
  const byId = new Map(roots.map((root) => [root.path, root]))
  const projectById = new Map(
    projects.map((project) => [project.projectId, project]),
  )
  const topLevel: TreeFolder[] = []
  for (const [index, project] of projects.entries()) {
    const root = roots[index] as TreeFolder
    const parent = project.parent && byId.get(project.parent.projectId)
    const inHome =
      !!opts.home && project.parent?.projectId === opts.home.projectId
    const seen = new Set([project.projectId])
    let ancestor = project.parent?.projectId
    while (ancestor && projectById.has(ancestor) && !seen.has(ancestor)) {
      seen.add(ancestor)
      ancestor = projectById.get(ancestor)?.parent?.projectId
    }
    if (
      (!parent && !inHome) ||
      seen.has(ancestor ?? '') ||
      pathSegments(project.parent!.folder).length === 0
    ) {
      topLevel.push(root)
      continue
    }
    const parentChildren = parent?.children ?? homeNodes
    const segments = pathSegments(project.parent!.folder)
    let children = parentChildren
    for (const [i, segment] of segments.slice(0, -1).entries()) {
      const path = segments.slice(0, i + 1).join('/')
      let folder = children.find(
        (node): node is TreeFolder =>
          node.kind === 'folder' && !node.root && node.path === path,
      )
      if (!folder) {
        folder = { kind: 'folder', name: segment, path, children: [] }
        children.push(folder)
      }
      children = folder.children
    }
    children.push(root)
  }
  for (const root of roots) root.children = sortNodes(root.children)
  const visible = [...sortNodes(homeNodes), ...topLevel]
  return skillsRoot ? [skillsRoot, ...visible] : visible
}

/**
 * The full path of the document with this id, or null.
 *
 * Walks the tree rather than the catalog so it returns the path AS THE TREE
 * BUILT IT: `pathSegments` normalises, and an ancestor set derived from a
 * differently-normalised string would miss the folders it is meant to open.
 */
export function findDocPath(
  nodes: TreeNode[],
  documentId: string | null,
  prefix = '',
): string | null {
  if (!documentId) return null
  for (const node of nodes) {
    if (node.kind === 'item') {
      // A skill row is a document too, and its folder must open with it.
      if (
        (node.item.type === 'skill' &&
          node.item.skill.documentId === documentId) ||
        (node.item.type === 'standing' && node.item.documentId === documentId)
      )
        return node.path
      continue
    }
    if (node.kind === 'file') {
      // Built from the ENCLOSING FOLDER's path, not from `doc.path`. Returning
      // the raw catalog path looks equivalent and is not: `/apps//deep/x.md`
      // normalises to folders `apps` and `apps/deep`, so ancestors derived from
      // the raw string match no folder in the tree and nothing opens.
      if (node.doc.id === documentId) {
        return prefix ? `${prefix}/${node.name}` : node.name
      }
      continue
    }
    const found = findDocPath(node.children, documentId, node.path)
    if (found) return found
  }
  return null
}

/** Every folder path in a tree — what "expand all" needs. */
export function allFolderPaths(nodes: TreeNode[]): string[] {
  return nodes.flatMap((n) =>
    n.kind === 'folder' ? [n.path, ...allFolderPaths(n.children)] : [],
  )
}
